import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Package, Truck, X, CheckCircle, ShoppingCart, Minus, Plus, Tag } from 'lucide-react';
import api from '../../api';
import { buildCategoryTree, getDescendantIds } from '../../utils/categoryTree';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const EMPTY_CHECKOUT = { client_name: '', client_phone: '', client_address: '', payment_preference: '', wants_delivery: false, notes: '' };

function PhotoCarousel({ photos, productName }) {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) {
    return (
      <div style={{ width: '100%', height: 220, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px 12px 0 0' }}>
        <Package size={52} strokeWidth={1} color="#bbb" />
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', height: 220, background: '#000', borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
      <img src={photos[idx]} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + photos.length) % photos.length); }} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft size={18} color="#fff" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % photos.length); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronRight size={18} color="#fff" />
          </button>
          <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4 }}>
            {photos.map((_, i) => (
              <div key={i} onClick={() => setIdx(i)} style={{ width: i === idx ? 16 : 6, height: 6, borderRadius: 3, background: i === idx ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'width 0.2s' }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CatalogView() {
  const { slug } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Cart state
  const [cart, setCart] = useState([]); // [{product, quantity}]
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState(EMPTY_CHECKOUT);
  const [checkoutErrors, setCheckoutErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  // Category filter
  const [activeCategory, setActiveCategory] = useState('all');
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [expandedCats, setExpandedCats] = useState(new Set());

  // ── Fix scroll: the app CSS locks overflow:hidden on body/#root ──
  useEffect(() => {
    document.body.classList.add('public-page');
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.maxWidth = '100%';
    }
    return () => {
      document.body.classList.remove('public-page');
      document.body.style.overflow = '';
      document.body.style.height = '';
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        root.style.maxWidth = '';
      }
    };
  }, []);

  useEffect(() => {
    api.get(`/catalog-public/${slug}`)
      .then(r => { setCatalog(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  // ── Cart helpers ──
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.product.price * i.quantity, 0);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) {
        return prev.map(i => i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQty = (productId, delta) => {
    setCart(prev => {
      const updated = prev.map(i => i.product.id === productId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i);
      return updated.filter(i => i.quantity > 0);
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(i => i.product.id !== productId));
  };

  const getCartQty = (productId) => {
    return cart.find(i => i.product.id === productId)?.quantity || 0;
  };

  const shippingCost = catalog?.global_shipping_cost || 0;
  const cartTotal = cartSubtotal + (checkoutForm.wants_delivery ? shippingCost : 0);

  // ── Checkout validation ──
  const validateCheckout = () => {
    const errs = {};
    if (!checkoutForm.client_name.trim()) errs.client_name = 'Requerido';
    if (!checkoutForm.client_phone.trim()) errs.client_phone = 'Requerido';
    if (checkoutForm.wants_delivery && !checkoutForm.client_address.trim()) errs.client_address = 'Requerido para envío';
    if (!checkoutForm.payment_preference) errs.payment_preference = 'Seleccioná una forma';
    return errs;
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    const errs = validateCheckout();
    if (Object.keys(errs).length > 0) { setCheckoutErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.post(`/catalog-public/${slug}/cart-order`, {
        items: cart.map(i => ({ product_id: i.product.id, quantity: i.quantity })),
        client_name: checkoutForm.client_name.trim(),
        client_phone: checkoutForm.client_phone.trim(),
        client_address: checkoutForm.client_address.trim() || null,
        payment_preference: checkoutForm.payment_preference,
        wants_delivery: checkoutForm.wants_delivery,
        notes: checkoutForm.notes.trim() || null,
      });
      setOrderSuccess({
        items: [...cart],
        total: cartTotal,
        delivery: checkoutForm.wants_delivery,
        shipping: shippingCost,
      });
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar el pedido');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
      <div className="spinner" />
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', padding: 24 }}>
      <div style={{ fontSize: 64 }}>🔍</div>
      <h2 style={{ marginTop: 16, color: '#333' }}>Catálogo no encontrado</h2>
      <p style={{ color: '#666', marginTop: 8 }}>Este link de catálogo no existe o fue desactivado.</p>
    </div>
  );

  if (orderSuccess) return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <CheckCircle size={72} color="#18cb96" strokeWidth={1.5} />
      <h2 style={{ marginTop: 16, color: '#222', textAlign: 'center' }}>¡Pedido enviado!</h2>
      <p style={{ color: '#555', marginTop: 8, textAlign: 'center', maxWidth: 320 }}>
        Tu pedido fue recibido. El vendedor se pondrá en contacto con vos pronto.
      </p>
      <div style={{ marginTop: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '16px 24px', width: '100%', maxWidth: 340 }}>
        {orderSuccess.items.map((i, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#333', padding: '4px 0' }}>
            <span>{i.product.name} × {i.quantity}</span>
            <span style={{ fontWeight: 600 }}>{fmt(i.product.price * i.quantity)}</span>
          </div>
        ))}
        {orderSuccess.delivery && orderSuccess.shipping > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#888', padding: '4px 0' }}>
            <span>Envío</span>
            <span>{fmt(orderSuccess.shipping)}</span>
          </div>
        )}
        <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', color: '#555' }}>Total</span>
          <span style={{ fontSize: '24px', fontWeight: 800, color: '#18cb96' }}>{fmt(orderSuccess.total)}</span>
        </div>
      </div>
      <button onClick={() => setOrderSuccess(null)} style={{ marginTop: 24, padding: '12px 32px', borderRadius: 10, background: '#18cb96', color: '#000', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '15px' }}>
        Ver más productos
      </button>
    </div>
  );

  // Filter products by category (selecting a parent also includes its subcategories)
  const allProducts = catalog.products;
  const categories = catalog.categories || [];
  const categoryTree = buildCategoryTree(categories);
  const categoryMap = {};
  categories.forEach(c => { categoryMap[c.id] = c; });
  const selectedCategoryName = activeCategory === 'all' ? 'Todos los productos' : (categoryMap[activeCategory]?.name || 'Todos los productos');
  const filteredProducts = activeCategory === 'all'
    ? allProducts
    : (() => {
        const ids = getDescendantIds(categories, activeCategory);
        return allProducts.filter(p => ids.includes(p.category_id));
      })();

  const toggleExpandCategory = (id) => setExpandedCats(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectCategory = (id) => { setActiveCategory(id); setCategoryMenuOpen(false); };

  const renderCategoryNode = (cat, depth) => {
    const children = categoryTree[cat.id] || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedCats.has(cat.id);
    const isActive = activeCategory === cat.id;
    return (
      <div key={cat.id}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 8 + depth * 20 }}>
          {hasChildren ? (
            <button
              data-testid={`category-expand-${cat.id}`}
              onClick={() => toggleExpandCategory(cat.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', color: '#999', flexShrink: 0 }}
            >
              <ChevronRight size={14} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
          ) : <span style={{ width: 30, flexShrink: 0 }} />}
          <button
            data-testid={`category-filter-${cat.id}`}
            onClick={() => selectCategory(cat.id)}
            style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '10px 8px', fontSize: '14px', fontWeight: isActive ? 700 : 500, color: isActive ? '#18cb96' : '#333' }}
          >
            {cat.name}
          </button>
        </div>
        {hasChildren && isExpanded && children.map(child => renderCategoryNode(child, depth + 1))}
      </div>
    );
  };

  const available = filteredProducts.filter(p => p.is_available);
  const unavailable = filteredProducts.filter(p => !p.is_available);

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ background: '#fff', padding: '24px 20px 20px', borderBottom: '1px solid #e8e8e8', textAlign: 'center' }}>
        {catalog.profile_image ? (
          <img src={catalog.profile_image} alt={catalog.business_name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #18cb96', marginBottom: 12 }} />
        ) : (
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #5de6c8, #18cb96)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '32px' }}>
            🛍️
          </div>
        )}
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#1a1a1a', margin: 0 }}>{catalog.business_name}</h1>
        <p style={{ fontSize: '14px', color: '#888', marginTop: 4 }}>{catalog.business_type}</p>
        <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f0fdf8', border: '1px solid #18cb96', borderRadius: 20, padding: '4px 12px', fontSize: '12px', color: '#18cb96', fontWeight: 600 }}>
          Catálogo Online
        </div>
      </div>

      {/* Category filter dropdown (nested categories/subcategories) */}
      {categories.length > 0 && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', position: 'relative' }}>
          <button
            data-testid="category-dropdown-toggle"
            onClick={() => setCategoryMenuOpen(v => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
              <Tag size={14} color="#18cb96" /> {selectedCategoryName}
            </span>
            <ChevronDown size={16} color="#888" style={{ transform: categoryMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {categoryMenuOpen && (
            <div style={{ borderTop: '1px solid #f0f0f0', maxHeight: '50vh', overflowY: 'auto', paddingBottom: 8 }}>
              <button
                data-testid="category-filter-all"
                onClick={() => selectCategory('all')}
                style={{ width: '100%', textAlign: 'left', background: activeCategory === 'all' ? '#f0fdf8' : 'none', border: 'none', cursor: 'pointer', padding: '10px 16px', fontSize: '14px', fontWeight: activeCategory === 'all' ? 700 : 500, color: activeCategory === 'all' ? '#18cb96' : '#333' }}
              >
                Todos los productos
              </button>
              {(categoryTree['root'] || []).map(cat => renderCategoryNode(cat, 0))}
            </div>
          )}
        </div>
      )}

      {/* Products */}
      <div style={{ padding: '16px', maxWidth: 600, margin: '0 auto' }}>
        {allProducts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <div style={{ fontSize: 48 }}>📦</div>
            <p style={{ marginTop: 12 }}>Este catálogo no tiene productos disponibles aún.</p>
          </div>
        )}

        {/* Available products */}
        {available.map(p => {
          const qtyInCart = getCartQty(p.id);
          return (
            <div key={p.id} style={{ background: '#fff', borderRadius: 14, marginBottom: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <PhotoCarousel photos={p.photos} productName={p.name} />
              <div style={{ padding: '14px 16px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1a1a1a', margin: 0, flex: 1 }}>{p.name}</h3>
                  <span style={{ fontSize: '18px', fontWeight: 800, color: '#18cb96', flexShrink: 0 }}>{fmt(p.price)}</span>
                </div>
                {p.description && <p style={{ fontSize: '14px', color: '#666', margin: '0 0 10px', lineHeight: 1.5 }}>{p.description}</p>}
                {p.category_name && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '11px', color: '#18cb96', background: '#f0fdf8', padding: '2px 8px', borderRadius: 10, marginBottom: 10 }}>
                    <Tag size={10} />{p.category_name}
                  </div>
                )}
                {p.stock_quantity !== null && (
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: 10 }}>
                    Stock: {p.stock_quantity}
                  </div>
                )}

                {/* Add to cart */}
                {qtyInCart === 0 ? (
                  <button
                    data-testid={`add-to-cart-${p.id}`}
                    onClick={() => addToCart(p)}
                    style={{ width: '100%', padding: '13px', borderRadius: 10, background: 'linear-gradient(135deg, #5de6c8, #18cb96)', color: '#000', border: 'none', fontWeight: 700, fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  >
                    <ShoppingCart size={18} /> Agregar al carrito
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f0fdf8', border: '2px solid #18cb96', borderRadius: 10, padding: '8px 14px', justifyContent: 'space-between' }}>
                    <button onClick={() => updateQty(p.id, -1)} style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid #18cb96', background: '#fff', cursor: 'pointer', fontSize: '18px', fontWeight: 700, color: '#18cb96', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Minus size={16} />
                    </button>
                    <span data-testid={`cart-qty-${p.id}`} style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', minWidth: 28, textAlign: 'center' }}>{qtyInCart}</span>
                    <button onClick={() => updateQty(p.id, 1)} style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid #18cb96', background: '#18cb96', cursor: 'pointer', fontSize: '18px', fontWeight: 700, color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Plus size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Unavailable products */}
        {unavailable.length > 0 && (
          <>
            <h3 style={{ fontSize: '14px', color: '#999', fontWeight: 600, margin: '16px 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sin stock</h3>
            {unavailable.map(p => (
              <div key={p.id} style={{ background: '#fff', borderRadius: 14, marginBottom: 12, overflow: 'hidden', opacity: 0.6 }}>
                {p.photos.length > 0 && <img src={p.photos[0]} alt={p.name} style={{ width: '100%', height: 160, objectFit: 'cover' }} />}
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#666', margin: 0 }}>{p.name}</h3>
                    <span style={{ fontSize: '13px', background: '#ffebeb', color: '#e53935', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>Sin stock</span>
                  </div>
                  {p.description && <p style={{ fontSize: '13px', color: '#999', marginTop: 4 }}>{p.description}</p>}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '20px 0', marginTop: 8 }}>
          <img src="/tuki-logo-dark.svg" alt="tuki" style={{ height: 28, opacity: 0.4 }} />
        </div>
      </div>

      {/* ── Floating Cart Button ── */}
      {cartCount > 0 && !showCart && !showCheckout && (
        <button
          data-testid="view-cart-btn"
          onClick={() => setShowCart(true)}
          style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #5de6c8, #18cb96)', color: '#000', border: 'none', borderRadius: 30, padding: '14px 28px', fontWeight: 800, fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 20px rgba(24,203,150,0.5)', display: 'flex', alignItems: 'center', gap: 10, zIndex: 100 }}
        >
          <ShoppingCart size={20} />
          Ver carrito
          <span style={{ background: '#000', color: '#18cb96', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800 }}>{cartCount}</span>
        </button>
      )}

      {/* ── CART MODAL ── */}
      {showCart && !showCheckout && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCart(false)}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: 500, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShoppingCart size={20} /> Mi Carrito
              </h3>
              <button onClick={() => setShowCart(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={22} /></button>
            </div>

            {cart.map(({ product, quantity }) => (
              <div key={product.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                {product.photos.length > 0 ? (
                  <img src={product.photos[0]} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: 8, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Package size={24} color="#bbb" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: '#18cb96', marginTop: 2 }}>{fmt(product.price * quantity)}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{fmt(product.price)} × {quantity}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => updateQty(product.id, -1)} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Minus size={14} color="#333" />
                  </button>
                  <span style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', minWidth: 20, textAlign: 'center' }}>{quantity}</span>
                  <button onClick={() => updateQty(product.id, 1)} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid #18cb96', background: '#18cb96', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plus size={14} color="#000" />
                  </button>
                </div>
              </div>
            ))}

            {/* Subtotal */}
            <div style={{ marginTop: 20, borderTop: '2px solid #f0f0f0', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>Subtotal</span>
                <span style={{ fontSize: '22px', fontWeight: 800, color: '#18cb96' }}>{fmt(cartSubtotal)}</span>
              </div>
              {shippingCost > 0 && (
                <div style={{ fontSize: '12px', color: '#888', marginBottom: 16, background: '#f8f8f8', borderRadius: 8, padding: '8px 12px' }}>
                  <Truck size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Envío disponible: <strong>{fmt(shippingCost)}</strong> (se agrega al confirmar)
                </div>
              )}
              <button
                data-testid="proceed-checkout-btn"
                onClick={() => { setShowCart(false); setShowCheckout(true); setCheckoutForm(EMPTY_CHECKOUT); setCheckoutErrors({}); }}
                style={{ width: '100%', padding: '15px', borderRadius: 12, background: 'linear-gradient(135deg, #5de6c8, #18cb96)', color: '#000', border: 'none', fontWeight: 800, fontSize: '16px', cursor: 'pointer' }}
              >
                Finalizar pedido
              </button>
              <button onClick={() => setShowCart(false)} style={{ width: '100%', padding: '12px', marginTop: 8, borderRadius: 10, background: 'none', border: '1px solid #ddd', color: '#888', fontWeight: 600, fontSize: '14px', cursor: 'pointer' }}>
                Seguir comprando
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CHECKOUT MODAL ── */}
      {showCheckout && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCheckout(false)}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: 500, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#1a1a1a' }}>Tus datos</h3>
              <button onClick={() => { setShowCheckout(false); setShowCart(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={22} /></button>
            </div>

            {/* Order summary */}
            <div style={{ background: '#f4f6f8', borderRadius: 10, padding: '12px 14px', marginBottom: 18 }}>
              {cart.map(({ product, quantity }) => (
                <div key={product.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#333', padding: '2px 0' }}>
                  <span>{product.name} × {quantity}</span>
                  <span style={{ fontWeight: 600 }}>{fmt(product.price * quantity)}</span>
                </div>
              ))}
            </div>

            <form onSubmit={submitOrder}>
              {/* Name */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>Nombre *</label>
                <input data-testid="checkout-name" value={checkoutForm.client_name} onChange={e => setCheckoutForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Tu nombre completo" style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: checkoutErrors.client_name ? '2px solid #e53935' : '1px solid #ddd', fontSize: '15px', outline: 'none', boxSizing: 'border-box', color: '#1a1a1a' }} />
                {checkoutErrors.client_name && <span style={{ fontSize: '12px', color: '#e53935' }}>{checkoutErrors.client_name}</span>}
              </div>

              {/* Phone */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>Celular *</label>
                <input data-testid="checkout-phone" type="tel" value={checkoutForm.client_phone} onChange={e => setCheckoutForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="Ej: 11 1234-5678" style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: checkoutErrors.client_phone ? '2px solid #e53935' : '1px solid #ddd', fontSize: '15px', outline: 'none', boxSizing: 'border-box', color: '#1a1a1a' }} />
                {checkoutErrors.client_phone && <span style={{ fontSize: '12px', color: '#e53935' }}>{checkoutErrors.client_phone}</span>}
              </div>

              {/* Payment preference */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: 8, display: 'block' }}>Forma de pago *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ k: 'efectivo', label: 'Efectivo', icon: '💵' }, { k: 'transferencia', label: 'Transferencia', icon: '📱' }].map(opt => (
                    <button key={opt.k} type="button" onClick={() => setCheckoutForm(f => ({ ...f, payment_preference: opt.k }))} style={{ flex: 1, padding: '12px 8px', borderRadius: 10, border: checkoutForm.payment_preference === opt.k ? '2px solid #18cb96' : '1px solid #ddd', background: checkoutForm.payment_preference === opt.k ? '#f0fdf8' : '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: checkoutForm.payment_preference === opt.k ? 700 : 500, color: checkoutForm.payment_preference === opt.k ? '#18cb96' : '#555' }}>
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                </div>
                {checkoutErrors.payment_preference && <span style={{ fontSize: '12px', color: '#e53935' }}>{checkoutErrors.payment_preference}</span>}
              </div>

              {/* Delivery */}
              {shippingCost > 0 && (
                <div style={{ marginBottom: 14, background: '#f4f6f8', borderRadius: 10, padding: '12px 14px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={checkoutForm.wants_delivery} onChange={e => setCheckoutForm(f => ({ ...f, wants_delivery: e.target.checked, client_address: '' }))} style={{ width: 18, height: 18, accentColor: '#18cb96' }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>Quiero envío a domicilio</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Costo de envío: {fmt(shippingCost)}</div>
                    </div>
                  </label>
                </div>
              )}

              {/* Address */}
              {checkoutForm.wants_delivery && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>Dirección de entrega *</label>
                  <input data-testid="checkout-address" value={checkoutForm.client_address} onChange={e => setCheckoutForm(f => ({ ...f, client_address: e.target.value }))} placeholder="Calle, número, ciudad..." style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: checkoutErrors.client_address ? '2px solid #e53935' : '1px solid #ddd', fontSize: '15px', outline: 'none', boxSizing: 'border-box', color: '#1a1a1a' }} />
                  {checkoutErrors.client_address && <span style={{ fontSize: '12px', color: '#e53935' }}>{checkoutErrors.client_address}</span>}
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: 6, display: 'block' }}>Notas (opcional)</label>
                <textarea value={checkoutForm.notes} onChange={e => setCheckoutForm(f => ({ ...f, notes: e.target.value }))} placeholder="Algún detalle especial del pedido..." rows={2} style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: '1px solid #ddd', fontSize: '14px', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1a1a1a' }} />
              </div>

              {/* Total */}
              <div style={{ background: '#f0fdf8', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: checkoutForm.wants_delivery ? 6 : 0 }}>
                  <span style={{ fontWeight: 600, color: '#333' }}>Subtotal</span>
                  <span style={{ fontWeight: 700, color: '#333' }}>{fmt(cartSubtotal)}</span>
                </div>
                {checkoutForm.wants_delivery && shippingCost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: '13px', color: '#888' }}>Envío</span>
                    <span style={{ fontSize: '13px', color: '#888' }}>{fmt(shippingCost)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: checkoutForm.wants_delivery ? '1px solid #d0f5e8' : 'none', paddingTop: checkoutForm.wants_delivery ? 8 : 0, marginTop: checkoutForm.wants_delivery ? 4 : 0 }}>
                  <span style={{ fontWeight: 700, color: '#333' }}>Total del pedido</span>
                  <span style={{ fontWeight: 800, fontSize: '22px', color: '#18cb96' }}>{fmt(cartTotal)}</span>
                </div>
              </div>

              <button data-testid="submit-cart-order-btn" type="submit" disabled={submitting} style={{ width: '100%', padding: '15px', borderRadius: 12, background: 'linear-gradient(135deg, #5de6c8, #18cb96)', color: '#000', border: 'none', fontWeight: 700, fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Enviando pedido...' : 'Confirmar Pedido'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
